import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';

import {
  type ComponentEntry,
  type ThemeChange,
} from '@/designs-dev/audit/types';
import Flow, { type Frame } from '@/karehero-app/tell-us-once/intake/Flow';
import ChatTyping from '@/components/wrapped/ChatTyping';
import AccountBar from '@/karehero-app/access/AccountBar';
import Attachment from '@/karehero-app/tell-us-once/intake/Attachment';
import ChatBubble from '@/karehero-app/tell-us-once/intake/ChatBubble';
import ChatMenuMessage from '@/karehero-app/tell-us-once/intake/ChatMenuMessage';
import ChatOption from '@/karehero-app/tell-us-once/intake/ChatOption';
import FailedMessage from '@/karehero-app/tell-us-once/intake/FailedMessage';
import FromUsCard from '@/karehero-app/tell-us-once/intake/FromUsCard';
import LiveCaptionCard from '@/karehero-app/tell-us-once/intake/LiveCaptionCard';
import QuickReplies from '@/karehero-app/tell-us-once/intake/QuickReplies';
import ReadingBubble from '@/karehero-app/tell-us-once/intake/ReadingBubble';
import VoiceNote from '@/karehero-app/tell-us-once/intake/VoiceNote';
import { HELP_READING_LINES } from '@/karehero-app/tell-us-once/intake/content.v2-0';
import { LIVE_CAPTION_SCRIPT } from '@/karehero-app/tell-us-once/intake/content.v2-0-dev';

const noop = () => {};

const PLACES = [
  { label: 'England' },
  { label: 'Scotland' },
  { label: 'Wales' },
  { label: 'Northern Ireland' },
];

const INTAKE = 'front-end/designs/src/karehero-app/tell-us-once/intake/';
const WRAPPED = 'front-end/designs/src/components/wrapped/';

/**
 * The bottom of a 2.0 screen, cropped to `height`: for a piece written inline
 * in the chat rather than as a component, shown as the real thing rather than
 * a copy of it.
 */
const bottomOf = (frame: Frame, height: number) => (
  <Box sx={{ position: 'relative', height, overflow: 'hidden' }}>
    <Box
      sx={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 700 }}
    >
      <Flow initial={frame} option={2} version='2.0-dev' />
    </Box>
  </Box>
);

const FILES: Record<string, string> = {
  ChatBubble: `${INTAKE}ChatBubble.tsx`,
  VoiceNote: `${INTAKE}VoiceNote.tsx`,
  Attachment: `${INTAKE}Attachment.tsx`,
  ChatMenuMessage: `${INTAKE}ChatMenuMessage.tsx`,
  ChatOption: `${INTAKE}ChatOption.tsx`,
  QuickReplies: `${INTAKE}QuickReplies.tsx`,
  FromUsCard: `${INTAKE}FromUsCard.tsx`,
  AccountBar: 'front-end/designs/src/karehero-app/access/AccountBar.tsx',
  ReadingBubble: `${INTAKE}ReadingBubble.tsx`,
  LiveCaptionCard: `${INTAKE}LiveCaptionCard.tsx`,
};

/**
 * Onboarding 2.0's components, each drawn on its own in the states worth
 * seeing, from the real component with made-up props.
 */
const STATES: Record<string, ComponentEntry['states']> = {
  ChatBubble: [
    {
      label: 'From Keni',
      node: <ChatBubble from='keni' text="What's going on at the moment?" />,
    },
    {
      label: 'From them',
      node: <ChatBubble from='user' text="I look after my mum, she's 84." />,
    },
    { label: 'Typing', node: <ChatTyping /> },
    {
      label: "Didn't send",
      node: (
        <FailedMessage onResend={noop}>
          <ChatBubble from='user' text='She needs help at night.' failed />
        </FailedMessage>
      ),
    },
  ],
  VoiceNote: [
    { label: 'Recording', node: <VoiceNote seconds={12} tone='recording' /> },
    { label: 'Ready to send', node: <VoiceNote seconds={34} tone='draft' /> },
    {
      label: 'Sent, with its words',
      node: (
        <VoiceNote
          seconds={34}
          transcript={LIVE_CAPTION_SCRIPT}
          transcriptShown
        />
      ),
    },
    { label: "Didn't send", node: <VoiceNote seconds={34} tone='failed' /> },
  ],
  Attachment: [
    {
      label: 'Photo, uploading',
      node: <Attachment kind='photo' state='uploading' />,
    },
    { label: 'Photo, sent', node: <Attachment kind='photo' /> },
    {
      label: 'File, sent',
      node: <Attachment kind='document' name='Blue badge letter.pdf' />,
    },
    {
      label: "Didn't upload",
      node: (
        <FailedMessage onResend={noop} label='Tap to try again'>
          <Attachment kind='photo' state='failed' />
        </FailedMessage>
      ),
    },
  ],
  Composer: [
    { label: 'Waiting for them', node: bottomOf('help-ask', 120) },
    { label: 'Typing', node: bottomOf('help-summary-typing', 120) },
    { label: 'Recording', node: bottomOf('help-recording', 120) },
    {
      label: 'Recording, captions on',
      node: bottomOf('help-recording-captions', 300),
    },
    { label: 'Quick replies above it', node: bottomOf('help-how', 180) },
  ],
  ChatMenuMessage: [
    {
      label: 'Asked',
      node: (
        <ChatMenuMessage
          text='Where in the UK does Margaret live?'
          options={PLACES}
          onPick={noop}
        />
      ),
    },
    {
      label: 'Answered',
      node: (
        <ChatMenuMessage
          text='Where in the UK does Margaret live?'
          options={PLACES}
          picked='England'
        />
      ),
    },
  ],
  ChatOption: [
    {
      label: 'An answer',
      node: <ChatOption label="That's fine with me" onClick={noop} />,
    },
    {
      label: 'Quieter',
      node: <ChatOption label="I'd rather not" quiet onClick={noop} />,
    },
    {
      label: 'Picked',
      node: <ChatOption label='England' picked onClick={noop} />,
    },
  ],
  QuickReplies: [
    {
      label: 'Yes or not now',
      node: (
        <QuickReplies
          replies={[
            { label: 'Yes' },
            { label: 'Not right now', secondary: true },
          ]}
          onPick={noop}
        />
      ),
    },
  ],
  FromUsCard: [
    {
      label: 'The summary',
      node: (
        <FromUsCard
          label='Does this look right?'
          sentence
          onEdit={noop}
          items={[
            "You're caring for your mum, about 40 hours a week.",
            "You'd like help with money she might be owed.",
          ]}
        />
      ),
    },
    {
      label: 'A checklist, pinned',
      node: (
        <FromUsCard
          label="What we'd need"
          sentence
          pinned
          checked={1}
          items={["Margaret's full name", "Margaret's date of birth", 'Her GP']}
        />
      ),
    },
  ],
  AccountBar: [{ label: 'In the chat', node: <AccountBar onSignOut={noop} /> }],
  ReadingBubble: [
    { label: 'Reading', node: <ReadingBubble lines={HELP_READING_LINES} /> },
  ],
  LiveCaptionCard: [
    {
      label: 'Listening',
      node: (
        <LiveCaptionCard
          words={LIVE_CAPTION_SCRIPT.split(' ').slice(0, 14)}
          listening
        />
      ),
    },
    {
      label: 'Stopped',
      node: (
        <LiveCaptionCard
          words={LIVE_CAPTION_SCRIPT.split(' ').slice(0, 14)}
          listening={false}
        />
      ),
    },
  ],
};

/** A line in the panel, for a component whose story is not in its states. */
const NOTES: Record<string, string> = {
  Composer:
    "Written inline at the bottom of ChatV2, not a component of its own yet, so there is no code to show here. Llama's ChatComposer does the same job.",
  ChatMenuMessage: 'This replaces ChatActionRow.',
};

/**
 * The theme changes onboarding 2.0 has in Loft that are not in Figma yet, as
 * before and now. 2.0's style sheet (IntakeStyle) and palettePreview.
 */
export const onboarding20ThemeChanges: ThemeChange[] = [
  {
    name: 'Gardenia Dark',
    now: { bg: 'custom.gardeniaDark', ink: 'text.primary' },
    hex: '#D7C7AD',
    note: 'A new colour, #D7C7AD. The reply bubbles use it.',
  },
  {
    name: 'Secondary text',
    was: {
      bg: 'background.paper',
      ink: (theme) => alpha(theme.palette.custom.neutral[700], 0.6),
    },
    now: { bg: 'background.paper', ink: 'text.secondary' },
    note: 'Charcoal at 77%, 5.2:1 on Floral White. It was 60%, 3.3:1, which failed.',
  },
];

export const onboarding20Components: ComponentEntry[] = Object.entries(
  STATES,
).map(([name, states]) => ({
  name,
  file: FILES[name],
  states,
  note: NOTES[name],
}));
